import React, { useContext, useEffect } from 'react';
import classnames from 'classnames';
import noop from 'lodash/noop';
import { DropdownStateContext } from '../../../DropdownStateManager';
import { DropdownGroup } from '../../../types';
import StandardMenuOption from '../StandardMenuOption';
import { useMenuItemHotkeys } from '../hooks';
import { itemWithOnSelect } from '../utils';
import s from './StandardMenuGroup.scss';

export interface Props {
  id: string;
  item: DropdownGroup;
  withHoverEffect?: boolean;
  hideCheckIcon?: boolean;
  className?: string;
  itemClassName?: string;
  focused?: boolean;
  startIndexOnFocus?: number;
  onPassGroupStart?: () => void;
  onPassGroupEnd?: () => void;
  onPressHome?: () => void;
  onPressEnd?: () => void;
  onSearch?: (character: string) => void;
  onSelect?: (value: any, label: any) => void;
}

const StandardMenuGroup: React.FC<Props> = ({
  id,
  item: { className: groupClassName, disabled: groupDisabled, items },
  className,
  itemClassName,
  hideCheckIcon,
  withHoverEffect,
  onPassGroupStart = noop,
  onPassGroupEnd = noop,
  focused,
  startIndexOnFocus,
  onSelect,
  onSearch,
  onPressHome,
  onPressEnd,
}) => {
  const { isOpen, firstKeyInteraction } = useContext(DropdownStateContext);

  const { handleKeyboardEvents, currentIndex, setCurrentIndex } = useMenuItemHotkeys(items.length, {
    onPassStart: () => {
      onPassGroupStart();
      setCurrentIndex(null);
    },
    onPassEnd: () => {
      onPassGroupEnd();
      setCurrentIndex(null);
    },
    onPressHome,
    onPressEnd,
    onSearch,
  });
  useEffect(() => {
    if (isOpen && focused) {
      setCurrentIndex(startIndexOnFocus ?? 0);
    } else {
      setCurrentIndex(null);
    }
  }, [isOpen, firstKeyInteraction, setCurrentIndex, startIndexOnFocus, focused]);

  return (
    <div
      className={classnames(s['menu-group'], groupClassName, className)}
      tabIndex={0}
      onKeyDown={e => {
        if (focused) {
          handleKeyboardEvents(e);
          e.stopPropagation();
        }
      }}
      role="group"
    >
      {items.map((item, idx) => (
        <StandardMenuOption
          id={`${id}-option-${idx}`}
          item={{
            ...itemWithOnSelect(item, onSelect),
            disabled: groupDisabled || item.disabled,
          }}
          className={itemClassName}
          hideCheckIcon={hideCheckIcon}
          withHoverEffect={withHoverEffect}
          focused={idx === currentIndex}
          key={`item_${item.value}_${idx}`}
        />
      ))}
    </div>
  );
};
StandardMenuGroup.displayName = 'StandardMenuGroup';

export default StandardMenuGroup;
